// The hold's chart symbol, and the light of a faction installation (R633,
// mockup round thirty-two, ui-map-interface.md §6.2).
//
// Mockup round thirty-two (`archive/mockups/032-hold-icons.html`) pitched ten
// chart conventions as the mark of a hold, each in a historical and a modern
// reading. The owner picked six of them as selectable hold symbols — cards
// 04 signal station, 05 beacon post, 06 rose, 07 cartouche, 08 harbour and 09
// dome — and card 02 "the light" as the mark of the three faction
// installations. This file holds the geometry of those seven cards.
//
// The rules the port keeps:
//
// 1. **The mockup is the reference.** Every offset, line weight, radius and
//    alpha below is the figure the card draws. A bezier (the dome's cupola),
//    an ellipse (the cartouche's seal) and a dashed outline (a ruin) are
//    sampled into `poly` and segments here, because `MarkGraphics` carries
//    none of the three.
// 2. **The path rule of `marks.ts`.** A paint applies to every sub-path since
//    the paint before it, so a shape that is filled and then outlined is
//    declared two times. `fillStroke` does that.
// 3. **The blink is a mark of its own.** `drawHoldMark` draws nothing that
//    moves. Everything the mockup multiplies by `blink(t)` — the white light,
//    the three arcs of the modern signal station, the rays of the historical
//    light — is drawn by `drawHoldLight` at full alpha, and the site layer
//    multiplies the alpha by the blink. No installation blinks (R641).
// 4. **The rank reads by shape and the faction by ink.** `drawHoldMark`
//    takes the faction ink; the white parts of the viewer's own hold are
//    white. A ruin carries its own ink (R126) and no plate.
// 5. **The plate is a theme field** (R834). `render/hold-rank-marks.ts`
//    holds the five own marks and the five squad marks, and `drawHoldMark`
//    lays the one the theme picked where the plate went. The order does not
//    change: the mark goes down before the symbol, which is R185's rule.
//
// One mark is empty on purpose: the modern cartouche of a ruin is text and a
// bar alone, and the layer draws both. The atlas gives an empty mark a
// one-texel cell, so the key stays legal.

import { cubic, dashedPoly, quadratic, starPoints, type MarkGraphics } from './marks';
import {
  isOwnHoldMark, isSquadHoldMark, OWN_HOLD_MARKS, SQUAD_HOLD_MARKS,
} from './hold-rank-marks';
import type { OwnHoldStyle, SquadHoldStyle } from '../ui/settings';
import { mulberry32 } from '../world';
import { CHART, MARK_INKS } from '../gen/presentation';
// `line()`, `disc()`, `circle()` and the round stroke of the mockup page,
// which the structure sets draw with as well.
import { arcPath, disc, line, ring, stroke } from './structures/shared';

/**
 * The AUTO value of the two hold-mark fields: the plate of R185.
 *
 * Position 0 of the generated list is the AUTO value by construction
 * (R822), and a test pins these two against it. They are literals here for
 * the reason `HOLD_SYMBOL_IDS` is one: the atlas and the menu previews draw
 * marks with no balance pack.
 */
const AUTO_OWN_HOLD: OwnHoldStyle = 'ground-plate';
const AUTO_SQUAD_HOLD: SquadHoldStyle = 'half-plate';

/** The reading of the chart setting (R548): historical or modern. */
export type HoldEra = 'h' | 'm';

/** What a hold is to the viewer. A ruin is not a rank; it is its own mark. */
export type HoldRank = 'own' | 'mate' | 'faction' | 'other';

/**
 * The six symbols a hold can wear, in the cycle order of the setting. The
 * first is the default.
 *
 * `data/identity/labels.toml` states the same list, and it reaches the client
 * as `HOLD_SYMBOLS` of `gen/balance.ts`. This module holds no generated
 * import, because the atlas and the menu previews draw marks with no balance
 * pack; a test pins the two lists equal instead.
 */
export const HOLD_SYMBOL_IDS = [
  'beacon-post', 'cartouche', 'signal-station', 'rose', 'harbour', 'dome',
] as const;

export type HoldSymbolId = typeof HOLD_SYMBOL_IDS[number];

/** The symbol a hold wears when nothing names one: the first of the list (R1279). */
export const DEFAULT_HOLD_SYMBOL: HoldSymbolId = HOLD_SYMBOL_IDS[0];

// ---------------------------------------------------------------------------
// The palette and the small helpers of the mockup page
// ---------------------------------------------------------------------------

// Every ink of this palette comes from `[presentation.mark_inks]`, so the
// light identity reaches the baked cell (§6.0,
// R3296): the white of an own mark is a dark core on paper, and the body is
// a paper knock-out under the faction edge.
/** The white of an own mark: its flag, its plate and its light. */
const WHITE = MARK_INKS.white;
/** The chart's magenta, which only a light and a Racon ring are drawn in. */
const MAGENTA = CHART.magenta;
/** The body fill of a hold, as `drawBase` of `marks.ts` uses it. */
const BODY_FILL = MARK_INKS.holdBody;
/** The ruin's blue-grey and its fill (R126). */
const RUIN_INK = MARK_INKS.ruinInk;
const RUIN_FILL = MARK_INKS.ruinFill;
/** The lantern room of the historical installation. */
const LANTERN = MARK_INKS.lantern;

/** What one rank draws in. It is `ctx(rank)` of the mockup page. */
interface RankInk {
  ink: number;
  fill: number;
  own: boolean;
  mate: boolean;
  /** The same-faction rank exactly. The modern beacon post reads it. */
  faction: boolean;
  ruin: boolean;
  /** The alpha of the body ink. A ruin is quieter. */
  a: number;
}

function rankInk(ink: number, rank: HoldRank): RankInk {
  return {
    ink,
    fill: BODY_FILL,
    own: rank === 'own',
    mate: rank === 'mate',
    faction: rank === 'faction',
    ruin: false,
    a: 0.9,
  };
}

const RUIN: RankInk = {
  ink: RUIN_INK, fill: RUIN_FILL,
  own: false, mate: false, faction: false, ruin: true, a: 0.75,
};

/**
 * Lays a path, fills it, lays it again and strokes it.
 *
 * The mockup fills and strokes one canvas path, because a 2D canvas keeps
 * the path over a paint. `MarkGraphics` follows the PixiJS rule instead and
 * starts a new path, so the shape is stated two times (see `marks.ts`).
 */
function fillStroke(
  g: MarkGraphics, path: () => void,
  fill: number, fillAlpha: number,
  ink: number, inkAlpha: number, width: number,
): void {
  path();
  g.fill({ color: fill, alpha: fillAlpha });
  path();
  stroke(g, ink, inkAlpha, width);
}

/** A rectangle as a polygon, in the argument order of `ctx.rect`. */
function rect(x: number, y: number, w: number, h: number): number[] {
  return [x, y, x + w, y, x + w, y + h, x, y + h];
}

/** How many segments an arc of this sweep is sampled into. */
const arcSteps = (sweep: number) => Math.max(6, Math.ceil(Math.abs(sweep) / (Math.PI / 48)));

/** An elliptical arc as a flat point list. A circle passes `rx === ry`. */
function ellipsePoints(
  cx: number, cy: number, rx: number, ry: number, a0: number, a1: number,
): number[] {
  const steps = arcSteps(a1 - a0);
  const pts: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    pts.push(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry);
  }
  return pts;
}

/**
 * The white light, at full alpha: a disc and a soft halo of 2.4 times the
 * radius. It is `light()` of the mockup page with the blink taken out.
 */
function lightAt(g: MarkGraphics, x: number, y: number, r: number): void {
  disc(g, x, y, r * 2.4, WHITE, 0.10);
  disc(g, x, y, r, WHITE, 1);
}

// ---------------------------------------------------------------------------
// The six hold symbols
// ---------------------------------------------------------------------------

/** Card 04, the signal station, in the line of the old sheet. */
function signalStationH(g: MarkGraphics, k: RankInk): void {
  fillStroke(g, () => { g.moveTo(-9, 12); g.arc(0, 12, 9, Math.PI, 0); },
    k.fill, 1, k.ink, k.a * 0.8, 1.1);
  if (k.ruin) {
    line(g, 0, 12, 6, -12, k.ink, k.a, 1.4);
    line(g, 1.5, -6, -6, -8, k.ink, k.a * 0.8, 1.1);
    return;
  }
  line(g, 0, 12, 0, -18, k.ink, k.a, 1.4);
  line(g, -9, -10, 9, -10, k.ink, k.a, 1.2);
  // The halyards carry one flag for a squadmate and two for the own hold.
  if (k.own || k.mate) g.poly(rect(-9, -9, 5, 4)).fill({ color: k.own ? WHITE : k.ink, alpha: 0.95 });
  if (k.own) g.poly(rect(4, -9, 5, 4)).fill({ color: k.ink, alpha: 0.95 });
  // The ball at the truck. The own hold's is the light, so it blinks.
  if (k.mate) disc(g, 0, -18, 1.8, k.ink, 0.95);
}

/** Card 04 in the line of the plotter: a mast that transmits. */
function signalStationM(g: MarkGraphics, k: RankInk): void {
  if (k.ruin) {
    fillStroke(g, () => { g.poly([-4, 11, 4, 11, 1.5, -2]); }, k.fill, 1, k.ink, k.a, 1.2);
    line(g, -1, -1, -4, -13, k.ink, k.a, 1.2);
    return;
  }
  fillStroke(g, () => { g.poly([-4, 11, 4, 11, 0, -12]); }, k.fill, 1, k.ink, k.a, 1.2);
  line(g, -2, 3, 2, 3, k.ink, k.a * 0.7, 0.9);
  line(g, -1, -4, 1, -4, k.ink, k.a * 0.7, 0.9);
  // Three arcs say the own hold, two a squadmate, one the rest. The own
  // hold's breathe with its light, so `drawHoldLight` draws them.
  if (!k.own) {
    const n = k.mate ? 2 : 1;
    for (let i = 0; i < n; i++) transmitArc(g, i, k.ink, 0.8);
    disc(g, 0, -12, 1.5, k.ink, 0.95);
  }
}

/** One arc of the modern signal station, counted out from the masthead. */
function transmitArc(g: MarkGraphics, i: number, color: number, alpha: number): void {
  arcPath(g, 0, -12, 4 + i * 3.2, -Math.PI * 0.85, -Math.PI * 0.15);
  stroke(g, color, alpha, 1.1);
}

/** Card 05, the beacon post on its mound. */
function beaconPostH(g: MarkGraphics, k: RankInk): void {
  fillStroke(g, () => { g.moveTo(-10, 12); g.arc(0, 12, 10, Math.PI, 0); },
    k.fill, 1, k.ink, k.a * 0.8, 1.1);
  for (let i = -7; i <= 7; i += 3.5) {
    line(g, i, 12, i + 1.5, 9 - Math.abs(i) * 0.5, k.ink, k.a * 0.4, 0.8);
  }
  if (k.ruin) { line(g, 0, 12, 7, -8, k.ink, k.a, 1.4); return; }
  line(g, 0, 12, 0, -9, k.ink, k.a, 1.4);
  // The topmark carries the rank: a ball for the two near ranks, a cone for
  // the rest. The own ball is white and the light stands on it.
  if (k.own || k.mate) disc(g, 0, -13, 4, k.own ? WHITE : k.ink, 0.95);
  else g.poly([-4, -9, 4, -9, 0, -17]).fill({ color: k.ink, alpha: 0.95 });
}

/** Card 05 in the line of the plotter: the topmark language of INT 1 Q 9. */
function beaconPostM(g: MarkGraphics, k: RankInk): void {
  if (k.ruin) {
    dashedPoly(g, [-6, 11, 6, 11, 0, 1], 2.5, 2.5, true);
    stroke(g, k.ink, k.a, 1.3);
    return;
  }
  g.poly([-6, 11, 6, 11, 0, 1]).fill({ color: k.ink, alpha: 0.95 });
  line(g, 0, 1, 0, -7, k.ink, k.a, 1.4);
  if (k.own) {
    // Safe water: the sphere on the post.
    disc(g, 0, -12, 4.5, WHITE, 0.95);
    line(g, 0, -16.5, 0, -7.5, k.ink, 0.9, 1.4);
  } else if (k.mate) {
    g.poly([-4.5, -7, 4.5, -7, 0, -16]).fill({ color: k.ink, alpha: 0.95 });
  } else if (k.faction) {
    g.poly(rect(-4, -15, 8, 8)).fill({ color: k.ink, alpha: 0.95 });
  } else {
    // The isolated-danger topmark, which the owner kept for another
    // faction's hold: two spheres.
    disc(g, 0, -10, 3, k.ink, 0.95);
    disc(g, 0, -17, 3, k.ink, 0.95);
  }
}

/** Card 06, the compass rose the navigator lays the rule from. */
function roseH(g: MarkGraphics, k: RankInk): void {
  // One point of the rose, as two half-blades: the lit half is filled in the
  // ink and the shaded half in the body fill.
  const blade = (ang: number, r: number, w: number): number[] => {
    const a = ang - Math.PI / 2;
    const ux = Math.cos(a), uy = Math.sin(a);
    return [0, 0, -uy * w, ux * w, ux * r, uy * r];
  };
  for (let i = 0; i < 8; i++) {
    const long = i % 2 === 0;
    if (k.ruin && long) continue; // a rose with its long points broken
    const r = k.ruin ? (long ? 6 : 8) : (long ? 15 : 9);
    const ang = (i * Math.PI) / 4;
    g.poly(blade(ang, r, 2.6)).fill({ color: k.ink, alpha: k.a });
    fillStroke(g, () => { g.poly(blade(ang, r, -2.6)); }, k.fill, 1, k.ink, k.a * 0.8, 0.8);
  }
  // The fleur-de-lis on the north point.
  if (k.own || k.mate) {
    g.poly([0, -16, 3, -9, 0, -11, -3, -9]).fill({ color: k.own ? WHITE : k.ink, alpha: 0.95 });
  }
  if (!k.ruin && !k.own) disc(g, 0, 0, 1.8, k.ink, 0.95);
}

/** Card 06 in the line of the plotter: the Racon of INT 1 S 3. */
function roseM(g: MarkGraphics, k: RankInk): void {
  if (k.ruin) {
    dashedPoly(g, ellipsePoints(0, 0, 9, 9, 0, Math.PI * 2), 2.5, 2.5, false);
    stroke(g, k.ink, k.a, 1.2);
    return;
  }
  disc(g, 0, 0, 3, k.own ? WHITE : k.ink, 0.95);
  if (k.own || k.mate) {
    ring(g, 0, 0, 8, k.ink, 0.9, 1.3);
  } else {
    arcPath(g, 0, 0, 8, -Math.PI * 0.9, -Math.PI * 0.4);
    stroke(g, k.ink, 0.9, 1.3);
  }
  // The magenta ring says the beacon answers a sweep. It stands still; the
  // light at the centre is the part that blinks.
  if (k.own) ring(g, 0, 0, 13, MAGENTA, 0.75, 1.2);
}

/** Card 07, the cartouche. The text in it belongs to the site layer. */
function cartoucheH(g: MarkGraphics, k: RankInk): void {
  line(g, 0, -14, 0, -9, k.ink, k.a, 1.2);
  ring(g, 0, -15.5, 2, k.own ? WHITE : k.ink, 0.95, 1.2);
  if (k.ruin) {
    // The torn seal: the oval is open on the lower right.
    g.poly(ellipsePoints(0, 1, 15, 10, Math.PI * 0.55, Math.PI * 1.85), false);
    stroke(g, k.ink, k.a, 1.2);
    return;
  }
  fillStroke(g, () => { g.poly(ellipsePoints(0, 1, 15, 10, 0, Math.PI * 2)); },
    k.fill, 1, k.own ? WHITE : k.ink, 0.9, 1.3);
  if (k.own) {
    g.poly(ellipsePoints(0, 1, 12.5, 7.8, 0, Math.PI * 2));
    stroke(g, WHITE, 0.6, 0.8);
  }
}

/**
 * Card 07 in the line of the plotter: the label box of INT 1 P 16.
 *
 * The mark is EMPTY in every rank. The box, the rule and the strike bar
 * follow the width of the designation, and a three-figure player id
 * ("K-142") is wider than any box a cell could bake, so the site layer
 * draws the frame round the run it laid out (`HOLD_LABEL_FRAME`). The
 * plate is still drawn under it by `drawHoldMark`.
 */
function cartoucheM(_g: MarkGraphics, _k: RankInk): void {}

/** The nine stipple dots of the basin, at the mockup's seed. */
const HARBOUR_STIPPLE: number[] = (() => {
  const rnd = mulberry32(7);
  const out: number[] = [];
  for (let i = 0; i < 9; i++) out.push(-5 + rnd() * 12, -4 + rnd() * 10);
  return out;
})();

/** The head of the historical mole, where the light stands. */
const HARBOUR_HEAD_X = Math.cos(Math.PI * 2.35) * 13;
const HARBOUR_HEAD_Y = Math.sin(Math.PI * 2.35) * 13;

/** Card 08, the harbour in plan: a curled mole round a stippled basin. */
function harbourH(g: MarkGraphics, k: RankInk): void {
  for (let i = 0; i < HARBOUR_STIPPLE.length; i += 2) {
    disc(g, HARBOUR_STIPPLE[i], HARBOUR_STIPPLE[i + 1], 0.7, k.ink, k.a * 0.6);
  }
  if (k.ruin) {
    // A mole broken in two.
    arcStroke(g, 13, Math.PI * 0.62, Math.PI * 1.55, k.ink, k.a, 2.2);
    arcStroke(g, 13, Math.PI * 1.7, Math.PI * 2.15, k.ink, k.a, 2.2);
    return;
  }
  arcStroke(g, 13, Math.PI * 0.62, Math.PI * 2.35, k.ink, k.a, 2.4);
  line(g, -13, 3, -5, 3, k.ink, k.a, 1.6);
  // The head of the mole. The own hold's head is the light.
  if (k.own) disc(g, HARBOUR_HEAD_X, HARBOUR_HEAD_Y, 3, WHITE, 0.95);
  else if (k.mate) disc(g, HARBOUR_HEAD_X, HARBOUR_HEAD_Y, 3, k.ink, 0.95);
}

/** One arc of the mole, centred on the origin and stroked by itself. */
function arcStroke(
  g: MarkGraphics, r: number, a0: number, a1: number,
  color: number, alpha: number, width: number,
): void {
  arcPath(g, 0, 0, r, a0, a1);
  stroke(g, color, alpha, width);
}

/** Card 08 in the line of the plotter: a basin open on one side. */
function harbourM(g: MarkGraphics, k: RankInk): void {
  // The basin: down the left wall, round the floor, up the right wall.
  const basin = (): void => {
    g.moveTo(-9, -10);
    g.lineTo(-9, 4);
    g.arc(0, 4, 9, Math.PI, 0, true);
    g.lineTo(9, -10);
  };
  if (k.ruin) {
    const pts = [-9, -10, -9, 4, ...ellipsePoints(0, 4, 9, 9, Math.PI, 0), 9, -10];
    dashedPoly(g, pts, 2.5, 2.5, false);
    stroke(g, k.ink, k.a, 1.5);
    return;
  }
  fillStroke(g, basin, k.fill, 0.7, k.ink, 0.9, 1.6);
  if (k.own || k.mate) line(g, -9, -3, 3, -3, k.own ? WHITE : k.ink, 0.95, 2);
  // The light stands at the mouth. Only the own hold's mouth blinks.
  if (!k.own) disc(g, 9, -10, 1.6, k.ink, 0.95);
}

/** Card 09, the cupola on its drum — the mark the client drew before R633. */
function domeH(g: MarkGraphics, k: RankInk): void {
  fillStroke(g, () => { g.poly(rect(-7, 3, 14, 7)); }, k.fill, 1, k.ink, k.a, 1.3);
  line(g, -4, 3, -4, 10, k.ink, k.a * 0.5, 0.8);
  line(g, 4, 3, 4, 10, k.ink, k.a * 0.5, 0.8);
  const cupola = (): void => {
    const pts = [-8, 3];
    cubic(pts, -8, 3, -8, -6, -3, -7, 0, -13, 16);
    cubic(pts, 0, -13, 3, -7, 8, -6, 8, 3, 16);
    g.poly(pts);
  };
  fillStroke(g, cupola, k.fill, 1, k.ink, k.a, 1.3);
  if (k.ruin) {
    // The crack down the dome.
    g.poly([-2, -10, 1, -6, -1, -2, 2, 2], false);
    stroke(g, k.ink, k.a, 1.1);
    return;
  }
  line(g, -5, -2, 5, -2, k.ink, k.a * 0.55, 0.8);
  if (k.own || k.mate) {
    const col = k.own ? WHITE : k.ink;
    line(g, 0, -13, 0, -18, col, 0.95, 1.4);
    disc(g, 0, -19, 2, col, 0.95);
  }
}

/** Card 09 in the line of the plotter: a half-disc on a base line. */
function domeM(g: MarkGraphics, k: RankInk): void {
  if (k.ruin) {
    dashedPoly(g, ellipsePoints(0, 5, 9, 9, Math.PI, Math.PI * 2), 2.5, 2.5, false);
    stroke(g, k.ink, k.a, 1.4);
    line(g, -10, 5, 10, 5, k.ink, k.a, 1.2);
    return;
  }
  fillStroke(g, () => { g.moveTo(-9, 5); g.arc(0, 5, 9, Math.PI, 0); g.closePath(); },
    k.fill, 1, k.ink, 0.9, 1.5);
  line(g, -11, 5, 11, 5, k.own ? WHITE : k.ink, 0.9, 1.6);
  if (k.own || k.mate) line(g, 0, -4, 0, -15, k.own ? WHITE : k.ink, 0.95, 1.5);
  if (!k.own) disc(g, 0, -4, 1.5, k.ink, 0.95);
}

/** The two readings of every symbol, by id. */
const SYMBOLS: Record<HoldSymbolId, Record<HoldEra, (g: MarkGraphics, k: RankInk) => void>> = {
  'cartouche': { h: cartoucheH, m: cartoucheM },
  'signal-station': { h: signalStationH, m: signalStationM },
  'beacon-post': { h: beaconPostH, m: beaconPostM },
  'rose': { h: roseH, m: roseM },
  'harbour': { h: harbourH, m: harbourM },
  'dome': { h: domeH, m: domeM },
};

/** Where the light of an own hold stands, by symbol and reading. */
const HOLD_LIGHTS: Record<HoldSymbolId, Record<HoldEra, { x: number; y: number; r: number }>> = {
  // The modern cartouche's light stands at the top-left corner of a box the
  // layer sizes to the text, so the cell holds it at the top edge's middle
  // and the layer moves it left by half the box (`lightAtFrameCorner`).
  'cartouche': { h: { x: 0, y: -15.5, r: 2 }, m: { x: 0, y: -7, r: 2 } },
  'signal-station': { h: { x: 0, y: -18, r: 2.4 }, m: { x: 0, y: -12, r: 2 } },
  'beacon-post': { h: { x: 0, y: -13, r: 2.2 }, m: { x: 0, y: -12, r: 1.6 } },
  'rose': { h: { x: 0, y: 0, r: 2.2 }, m: { x: 0, y: 0, r: 2 } },
  'harbour': { h: { x: HARBOUR_HEAD_X, y: HARBOUR_HEAD_Y, r: 1.8 }, m: { x: 9, y: -10, r: 2 } },
  'dome': { h: { x: 0, y: -19, r: 1.8 }, m: { x: 0, y: -15, r: 2 } },
};

/**
 * The hold at its own cell centre: the rank's own mark, then the symbol.
 *
 * The mark (R185) goes down FIRST, because it goes under everything. The
 * AUTO pair is the plate: r27 with a white rim for the viewer's own hold,
 * r25 without one for a squadmate's, and none for the two other ranks.
 *
 * `mark` picks which mark of that rank the theme wears (R834): a value of
 * `ownHold` at the own rank, a value of `squadHold` at the mate rank. A
 * caller with no theme — the menu scene — leaves it out and takes the AUTO
 * pair. The two plain ranks wear no mark, whatever it says.
 *
 * `ink` is the faction ink. Nothing drawn here moves.
 */
export function drawHoldMark(
  g: MarkGraphics, symbol: HoldSymbolId, era: HoldEra, ink: number, rank: HoldRank,
  mark?: OwnHoldStyle | SquadHoldStyle,
): void {
  if (rank === 'own') {
    const m = mark !== undefined && isOwnHoldMark(mark) ? mark : AUTO_OWN_HOLD;
    OWN_HOLD_MARKS[m].still(g, ink);
  } else if (rank === 'mate') {
    const m = mark !== undefined && isSquadHoldMark(mark) ? mark : AUTO_SQUAD_HOLD;
    SQUAD_HOLD_MARKS[m].still(g, ink);
  }
  SYMBOLS[symbol][era](g, rankInk(ink, rank));
}

/**
 * The parts of an own hold that blink, at full alpha.
 *
 * The site layer multiplies the alpha by the beacon blink of §6.2, so this
 * mark is baked one time and the movement costs one alpha a frame.
 */
export function drawHoldLight(g: MarkGraphics, symbol: HoldSymbolId, era: HoldEra): void {
  // The modern signal station transmits three arcs, and the mockup breathes
  // them with the light. They are drawn at the middle of the alpha the card
  // gives them (0.55 to 0.90), because the layer owns the swing.
  if (symbol === 'signal-station' && era === 'm') {
    for (let i = 0; i < 3; i++) transmitArc(g, i, WHITE, 0.9);
  }
  const at = HOLD_LIGHTS[symbol][era];
  lightAt(g, at.x, at.y, at.r);
}

/** A fallen hold (R126): the symbol broken, in the ruin's blue-grey. */
export function drawHoldRuin(g: MarkGraphics, symbol: HoldSymbolId, era: HoldEra): void {
  SYMBOLS[symbol][era](g, RUIN);
}

// ---------------------------------------------------------------------------
// Card 02, the light: the mark of a faction installation
// ---------------------------------------------------------------------------
//
// The owner gave card 02 to the three faction installations (`SITE_KIND_BASE`).
// The viewer's OWN faction wears the card's "squadmate" variant — the four
// short rays in the faction ink, the star with the short flare — and every
// other faction wears the plain variant in its own ink (R641). The own-hold
// variant was too loud for a structure that stands on every sheet. No
// installation carries the plate, because the plate says "yours" about a
// hold, and no installation blinks, because the squadmate variant is still.

/** The angle the modern flare points along. */
const FLARE_ANGLE = -Math.PI * 0.35;
/** How far the flare of the viewer's own faction reaches. */
const FLARE_LEN = 11;
/** How many rays the historical lantern shows, and how long they are. */
const RAY_COUNT = 4;
const RAY_LEN = 7;
/** The alpha of those rays. They are ink, not light, so they are quieter. */
const RAY_ALPHA = 0.55;

/** The tower of the historical light, without the lantern above it. */
function towerBody(g: MarkGraphics, k: RankInk, top: number): void {
  fillStroke(g, () => { g.poly([-7, 11, 7, 11, 4, top, -4, top]); }, k.fill, 1, k.ink, k.a, 1.4);
  line(g, -6.5, 1, 6.5, 1, k.ink, k.a * 0.7, 1);
  line(g, -7, 6, 7, 6, k.ink, k.a * 0.7, 1);
}

/**
 * A faction installation (card 02). `own` says the viewer's own faction, and
 * it takes the card's squadmate variant without the plate (R641).
 */
export function drawInstallationMark(
  g: MarkGraphics, era: HoldEra, ink: number, own: boolean,
): void {
  const k = rankInk(ink, own ? 'mate' : 'faction');
  if (era === 'h') {
    towerBody(g, k, -7);
    line(g, -6, -7, 6, -7, k.ink, k.a, 1.6); // the gallery
    fillStroke(g, () => { g.poly(rect(-3, -13, 6, 6)); }, LANTERN, 1, k.ink, k.a, 1.2);
    g.poly([-4, -13, 0, -17, 4, -13]).fill({ color: k.ink, alpha: k.a });
    // The squadmate rays are ink and they stand still, so the mark holds
    // them itself. Every installation keeps its lantern dot.
    if (own) {
      for (let i = 0; i < RAY_COUNT; i++) {
        const a = -Math.PI * 0.95 + (i * Math.PI * 0.9) / (RAY_COUNT - 1);
        line(g, Math.cos(a) * 6, -10 + Math.sin(a) * 6,
          Math.cos(a) * (6 + RAY_LEN), -10 + Math.sin(a) * (6 + RAY_LEN),
          k.ink, RAY_ALPHA, 1.1);
      }
    }
    disc(g, 0, -10, 1.6, k.ink, 0.95);
    return;
  }
  if (own) {
    const c = Math.cos(FLARE_ANGLE), s = Math.sin(FLARE_ANGLE);
    const pts: number[] = [0, -3.5];
    quadratic(pts, 0, -3.5, FLARE_LEN * 0.55, -4.5, FLARE_LEN, 0);
    quadratic(pts, FLARE_LEN, 0, FLARE_LEN * 0.55, 4.5, 0, 3.5);
    const turned: number[] = [];
    for (let i = 0; i < pts.length; i += 2) {
      turned.push(pts[i] * c - pts[i + 1] * s, pts[i] * s + pts[i + 1] * c);
    }
    g.poly(turned).fill({ color: MAGENTA, alpha: 0.85 });
  }
  g.poly(starPoints(8, 8, 3.4, -Math.PI / 2)).fill({ color: ink, alpha: 0.9 });
}

/** A fallen installation: the lantern is gone, or the star is hollow. */
export function drawInstallationRuin(g: MarkGraphics, era: HoldEra): void {
  if (era === 'h') {
    towerBody(g, RUIN, -4);
    g.poly([-4, -4, -2, -7, 0, -3, 2, -6, 4, -4], false);
    stroke(g, RUIN.ink, RUIN.a, 1.2);
    return;
  }
  g.poly(starPoints(8, 8, 3.4, -Math.PI / 2));
  stroke(g, RUIN.ink, RUIN.a, 1.3);
}

// ---------------------------------------------------------------------------
// The text slot of the cartouche
// ---------------------------------------------------------------------------

/** Where and how the site layer writes the text of one cartouche. */
export interface HoldLabelSlot {
  /** The vendored chart face the run is set in (R548). */
  face: 'fell' | 'mono';
  sizePx: number;
  dx: number;
  dy: number;
  /** The own rank writes in white, as every white part of the mark does. */
  white: boolean;
  /** The modern ruin is struck through. `HOLD_LABEL_STRIKE` is the bar. */
  strike: boolean;
  anchor: 'center';
  /**
   * What the layer draws round the run, sized to it (`HOLD_LABEL_FRAME`):
   * the box of an own or a squadmate's hold, the rule under a faction's or
   * another faction's designation, or nothing.
   */
  frame: 'box' | 'rule' | 'none';
  /** The light sprite moves to the top-left corner of the box. */
  lightAtFrameCorner: boolean;
}

/**
 * The frame of the modern cartouche, in world px at 1x. The inner width is
 * the run's width or `minInner`, whichever is wider, so a short designation
 * keeps the 26 px box of the mockup and a long one grows with its text.
 */
export const HOLD_LABEL_FRAME = {
  /** The box: its top edge, its height, and the ink between text and edge. */
  top: -7, height: 14, padX: 3, minInner: 20, width: 1.3, alpha: 0.9,
  /** The box's fill, the dome fill of every mark. */
  fill: BODY_FILL, fillAlpha: 1,
  /** The rule under a designation with no box: its offset and its inset. */
  ruleDy: 8, ruleInset: 2, ruleWidth: 1.2,
} as const;

/** The bar the layer draws across a struck-out designation: the run plus a pad each side. */
export const HOLD_LABEL_STRIKE = {
  pad: 1, y: 0, width: 1.4, alpha: 0.9,
} as const;

/**
 * The text slot of a hold mark, or `null` where the symbol carries no text.
 *
 * The cartouche is the one symbol that names its owner: the commander's
 * initials in the Fell face of the old sheet, or the empire's designation in
 * the mono face of the plotter. The layer builds the string from
 * `Site.commander` and draws it as a glyph run, because a baked cell would
 * need one atlas cell for every name on the map.
 */
export function holdLabel(
  symbol: HoldSymbolId, era: HoldEra, rank: HoldRank | 'ruin',
): HoldLabelSlot | null {
  if (symbol !== 'cartouche') return null;
  if (era === 'h') {
    return {
      face: 'fell', sizePx: 10, dx: 0, dy: 2, white: rank === 'own', strike: false,
      anchor: 'center', frame: 'none', lightAtFrameCorner: false,
    };
  }
  // The modern ruin sits a half pixel higher, because the strike bar runs
  // through the middle of the run at y 0.
  const ruin = rank === 'ruin';
  return {
    face: 'mono', sizePx: 10, dx: 0, dy: ruin ? 0 : 0.5,
    white: rank === 'own', strike: ruin, anchor: 'center',
    frame: ruin ? 'none' : (rank === 'own' || rank === 'mate' ? 'box' : 'rule'),
    lightAtFrameCorner: rank === 'own',
  };
}
